import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth, onAuthStateChanged, signInWithEmailAndPassword,
  signInWithPopup, GoogleAuthProvider, signOut, type User as FbUser,
} from 'firebase/auth';
import { track } from '../utils/track';

/** Новый аккаунт: creationTime ≈ lastSignInTime (в пределах ~5с). */
function isNewAccount(fb: FbUser): boolean {
  const c = fb.metadata.creationTime;
  const l = fb.metadata.lastSignInTime;
  if (!c || !l) return false;
  return Math.abs(new Date(c).getTime() - new Date(l).getTime()) < 5_000;
}

interface TeleclawUser {
  uid: string;
  email: string | null;
  plan: string;
  farmEnabled: boolean;
  isAdmin: boolean;
}

interface AuthContext {
  user: TeleclawUser | null;
  loading: boolean;
  signInEmail: (email: string, password: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  isAdmin: boolean;
}

const Ctx = createContext<AuthContext | null>(null);

let app: FirebaseApp | null = null;
function getFirebase(): FirebaseApp {
  if (!app) {
    app = initializeApp({
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    });
  }
  return app;
}

function mapUser(fb: FbUser, claims?: { plan?: string; farm_enabled?: boolean; admin?: boolean }): TeleclawUser {
  return {
    uid: fb.uid,
    email: fb.email,
    plan: claims?.plan ?? 'free',
    farmEnabled: Boolean(claims?.farm_enabled),
    isAdmin: claims?.admin === true,
  };
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<TeleclawUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth(getFirebase());
    const unsub = onAuthStateChanged(auth, async (fb) => {
      if (!fb) {
        setUser(null);
      } else {
        const idTokenResult = await fb.getIdTokenResult();
        setUser(mapUser(fb, idTokenResult.claims as { plan?: string; farm_enabled?: boolean; admin?: boolean }));
        // §7a signup — один раз на новый аккаунт (guard от повторов через localStorage).
        const guardKey = `teleclaw_signup_tracked_${fb.uid}`;
        if (isNewAccount(fb) && !localStorage.getItem(guardKey)) {
          try { localStorage.setItem(guardKey, '1'); } catch { /* ignore */ }
          track('signup', { method: fb.providerData[0]?.providerId ?? 'password' });
        }
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const isAdmin = user?.isAdmin === true;

  const value: AuthContext = {
    user,
    loading,
    isAdmin,
    signInEmail: async (email, password) => {
      const auth = getAuth(getFirebase());
      await signInWithEmailAndPassword(auth, email, password);
    },
    signInGoogle: async () => {
      const auth = getAuth(getFirebase());
      await signInWithPopup(auth, new GoogleAuthProvider());
    },
    signOut: async () => {
      const auth = getAuth(getFirebase());
      await signOut(auth);
    },
    getIdToken: async () => {
      const auth = getAuth(getFirebase());
      return auth.currentUser?.getIdToken() ?? null;
    },
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
